import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ModelOptionsLoaderService } from '../../services/model-options-loader/model-options-loader.service';
import { UserMasterInstanceToken } from '../../interfaces/user-master.interface';
import { JsonTextModel, TextModel } from '../primitive/text.model';
import { CompositeModel } from './composite.model';
import { DropdownModel, JsonDropdownModel } from './dropdown.model';
import { AddressModel } from './address.model';
import { hasBackendAddrNumber } from '../../state/address/address-number';
import { isDefined } from '../../utilities/is-defined';

export class TimesheetAddressModel extends CompositeModel {
    public street: TextModel;
    public city: TextModel;
    public postCode: TextModel;
    public country: TextModel;
    public addrText: TextModel;
    public addrNumber: TextModel;
    public statesOfUSA: DropdownModel<TextModel> | undefined;
    public addrKey?: string;

    public constructor(
        street = '',
        city = '',
        postCode = '',
        country = '',
        addrText = '',
        addrNumber = '',
        state = '',
        addrKey = '',
    ) {
        super('TimesheetAddressModel');

        this.street = <TextModel>this.set(new TextModel('Street', street));
        this.city = <TextModel>this.set(new TextModel('City', city));
        this.postCode = <TextModel>this.set(new TextModel('PostalCode', postCode));
        this.country = <TextModel>this.set(new TextModel('Country', country));
        this.addrText = <TextModel>this.set(new TextModel('AddrNotes', addrText));
        this.addrNumber = <TextModel>this.set(new TextModel('AddrNumber', addrNumber));
        this.addrKey = addrKey;
        AddressModel.updateStateOfUSA(this, state);

        const injector = CustomInjector.getInjector();
        const modelOptionsLoader = injector.get(ModelOptionsLoaderService);
        const userMasterService = injector.get(UserMasterInstanceToken);

        modelOptionsLoader.load(this.street, 'Timesheet', 'Street');
        modelOptionsLoader.load(this.city, 'Timesheet', 'City');
        modelOptionsLoader.load(this.postCode, 'Timesheet', 'PostCode');
        modelOptionsLoader.load(this.country, 'Timesheet', 'Country');
        modelOptionsLoader.load(this.addrText, 'Timesheet', 'AddrText');

        // For release 1 country field is mandatory for new addresses and populated from user data table pernr (which has value 'DE' for all users) and is non-editable.
        this.country.value = userMasterService.getSelectedUserModel().getCountry();
        this.country.options = {
            ...this.country.options,
            readonly: true,
        };

        if (isDefined(this.statesOfUSA)) {
            modelOptionsLoader.load(this.statesOfUSA, 'Timesheet', 'State');
            this.statesOfUSA.options = {
                ...this.statesOfUSA.options,
                disabled: hasBackendAddrNumber(this.addrNumber.value),
            };
        }
    }

    public isValid(): boolean {
        return this.street.value.length > 0 && this.city.value.length > 0 && this.postCode.value.length > 0;
    }

    public static compare(firstAddress: TimesheetAddressModel, secondAddress: TimesheetAddressModel): boolean {
        return (
            firstAddress.street.value === secondAddress.street.value &&
            firstAddress.city.value === secondAddress.city.value &&
            firstAddress.postCode.value === secondAddress.postCode.value &&
            firstAddress.country.value === secondAddress.country.value &&
            firstAddress.addrText.value === secondAddress.addrText.value &&
            firstAddress.statesOfUSA?.selectedEntry?.model.value ===
                secondAddress.statesOfUSA?.selectedEntry?.model.value
        );
    }

    public equalsTo(other: TimesheetAddressModel): boolean {
        return TimesheetAddressModel.compare(this, other);
    }

    public toJSON(): JsonTimesheetAddressModel {
        return {
            street: this.street.value,
            city: this.city.value,
            postCode: this.postCode.value,
            country: this.country.value,
            addrText: this.addrText.value,
            addrNumber: this.addrNumber.value,
            statesOfUSA: this.statesOfUSA?.selectedEntry?.model.value ?? '',
            addrKey: this.addrKey,
        };
    }

    public getOutModel(): ODataTimesheetAddressOutModel {
        const outModel = <ODataTimesheetAddressOutModel>super.getOutModel();
        // Selected StateOfUSA should be sent to backend in city field in <city>;<state> format
        // Example: "Los Angels;CA"
        if (this.statesOfUSA && this.statesOfUSA.selectedEntry?.model.value) {
            outModel[this.city.name] = `${this.city.value};${this.statesOfUSA.selectedEntry.model.value}`;
        }
        return outModel;
    }
}

export class TimesheetAddressModelFromJson extends TimesheetAddressModel {
    public constructor(data: JsonTimesheetAddressModel | JsonTimesheetAddressModelWithObject) {
        // A toJSON() method was recently added to the dropdown model.
        // Because of this, data.statesOfUSA.selectedEntries gets stringified, and accessing [0] returns the first character instead of array[0].model.value.
        // To address this, a toJSON() implementation is also added here so that only the raw values are stored, instead of the full TextModel with options.
        // This change introduces a need for migration logic, since older data is still stored in object form.
        // The migration logic can be removed after (4.32.0) a few production releases, once legacy data is no longer in use.
        const stateOfUSA = (
            isDefined(data.statesOfUSA) && typeof data.statesOfUSA === 'object'
                ? (data.statesOfUSA.selectedEntries?.[0]?.model?.value ?? '')
                : data.statesOfUSA
        ) as string;
        super(
            isDefined(data.street) && typeof data.street === 'object' ? data.street.value : data.street,
            isDefined(data.city) && typeof data.city === 'object' ? data.city.value : data.city,
            isDefined(data.postCode) && typeof data.postCode === 'object' ? data.postCode.value : data.postCode,
            isDefined(data.country) && typeof data.country === 'object' ? data.country.value : data.country,
            isDefined(data.addrText) && typeof data.addrText === 'object' ? data.addrText.value : data.addrText,
            isDefined(data.addrNumber) && typeof data.addrNumber === 'object' ? data.addrNumber.value : data.addrNumber,
            stateOfUSA,
            isDefined(data.addrKey) ? data.addrKey : '',
        );
    }
}

export interface JsonTimesheetAddressModel {
    street: string;
    city: string;
    postCode: string;
    country: string;
    addrText: string;
    addrNumber: string;
    statesOfUSA?: string;
    addrKey?: string;
    allowToHide?: boolean;
}

export interface JsonTimesheetAddressModelWithObject {
    street: JsonTextModel;
    city: JsonTextModel;
    postCode: JsonTextModel;
    country: JsonTextModel;
    addrText: JsonTextModel;
    addrNumber: JsonTextModel;
    statesOfUSA: JsonDropdownModel;
    addrKey?: string;
}

export interface ODataTimesheetAddressOutModel extends ODataObject {
    Street: string;
    City: string;
    PostalCode: string;
    Country: string;
    AddrNotes: string;
    AddrNumber: string;
}
